/**
 * ReauthGate
 *
 * Wrapper that hard-blocks rendering of JTL-dependent pages while a
 * workspace integration is awaiting re-authorization. Instead of showing
 * stale/broken product data, we render a full-page block screen that
 * funnels the user straight to the re-authorization flow.
 *
 * Usage:
 *   <ReauthGate>
 *     <ProductDetailPage />
 *   </ReauthGate>
 *
 * A11y: block screen is announced as role="alert" so screen-readers
 * notice the navigation interruption; the CTA is the natural next focus.
 */

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui/Button'
import { useReauthStatus } from '@/hooks/useReauthStatus'

interface ReauthGateProps {
  children: ReactNode
}

export function ReauthGate({ children }: ReauthGateProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { needsReauth } = useReauthStatus()

  if (!needsReauth) return <>{children}</>

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-12"
    >
      <div className="max-w-lg w-full text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
          <ShieldAlert
            className="h-10 w-10 text-amber-600 dark:text-amber-400"
            aria-hidden="true"
          />
        </div>

        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-3">
          {t('reauth.gate.title')}
        </h1>

        <p className="text-base text-neutral-600 dark:text-neutral-400 mb-8">
          {t('reauth.gate.description')}
        </p>

        <Button
          variant="primary"
          size="lg"
          onClick={() => navigate('/v2/workspace-settings?tab=integrations')}
          autoFocus
        >
          <ShieldAlert className="h-5 w-5 mr-2" aria-hidden="true" />
          {t('reauth.gate.cta')}
        </Button>
      </div>
    </div>
  )
}

export default ReauthGate
